import $ from 'jquery';

import Decimal from 'app/src/data/base/Decimal';
import CommonData from 'app/src/data/CommonData';
import Format from 'app/src/data/Format';
import PaymentModel from 'app/src/data/models/Payment';
import SalePayment from 'app/src/data/models/SalePayment';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';

/**
 * Base payment class.  Concrete types of payments are derived from this class.
 */
export default class Payment {
  /**
   * Constructor for a Payment which wraps payment logic around a payment
   * model.
   *
   * @param amount {Decimal}  The payment amount
   * @param paymentTypeID {Number}   The ID of the charge
   * @param rawResponse {String} The raw response received from the terminal from a transaction request
   * @param saleID {Number} The ID of the charge
   * @param tipAmount {Decimal} The payment tipAMount
   */
  constructor(
    amount = new Decimal(0),
    paymentTypeID = 0,
    rawResponse = null,
    saleID = 0,
    tipAmount = null
  ) {
    amount = new Decimal(amount);
    const employeeID = window.merchantos.session.getEmployee().employeeID;
    const shopID = CommonData.selectedShop.id;
    const registerID = CommonData.selectedRegister.id;

    this._model = new SalePayment({
      amount,
      paymentTypeID,
      // model defaults to session defaults; in the future it might make sense to
      // to pass these in via the constructor params
      accountID: window.merchantos.session.getSystemCustomerID(),
      saleID,
      employeeID,
      registerID,
      ...(tipAmount && { tipAmount }),
    });

    this._serviceModel = new PaymentModel({
      totalAmount: amount,
      saleID,
      // TODO: Eventually the payment service will take care of setting the payment type
      // but for now just set it to the credit card type
      paymentTypeID,
      shopID,
      registerID,
      ...(tipAmount && { tipAmount }),
    });

    // the sale manager needs the payment type information
    let paymentType = PaymentTypeManager.getPaymentType(Format.parseInt(paymentTypeID));
    if (!this._model.has('PaymentType') && paymentType) {
      this._model.set('PaymentType', paymentType.toJSON());
    }

    this._commitImmediately = true;
    this._allowMultiple = true;
    this._refundTip = false;
  }

  /**
   * Retrieves whether the payment is committed immediately to server once it is created as
   * opposed to when the sale is completed.
   *
   * @returns {boolean} True if the payment can be committed immediately to the server.
   */
  get commitImmediately() {
    return this._commitImmediately;
  }

  /**
   *  Returns whether the payment can have multiple occurrences within a sale.
   *
   * @returns {boolean} True if the payment can have multiple instances
   */
  get allowMultiple() {
    return this._allowMultiple;
  }

  /**
   * Getter for the contained payment model
   *
   * @returns {Backbone.Model}    The model contained by the Payment object
   */
  get model() {
    return this._model;
  }

  /**
   * Setter for the contained payment model
   *
   * @param model {Backbone.Model}    The payment model
   */
  set model(model) {
    this._model = model;
  }

  get serviceModel() {
    return this._serviceModel;
  }

  set serviceModel(serviceModel) {
    this._serviceModel = serviceModel;
  }

  /**
   * Getter for the amount of the payment.
   *
   * @returns {Number}    The payment amount.
   */
  get amount() {
    return this._model.get('amount');
  }

  /**
   * Setter for the amount of the payment.
   *
   * @param amount {Number}   The payment amount.
   */
  set amount(amount) {
    this._model.set('amount', new Decimal(amount));
  }

  /**
   * Getter for the tipAmount of the payment.
   *
   * @returns {Number} The payment tipAmount.
   */
  get tipAmount() {
    return this._model.get('tipAmount');
  }

  /**
   * Setter for the tipAmount of the payment.
   *
   * @param tipAmount {Number} The payment tipAmount.
   * @return tipAmount {Decimal}
   */
  set tipAmount(tipAmount) {
    this._model.set('tipAmount', new Decimal(tipAmount));
  }

  /**
   * Getter for the payment type ID of the payment.
   *
   * @returns {Number}    The payment type ID of the payment.
   */
  get paymentTypeID() {
    return this._model.get('paymentTypeID');
  }

  /**
   * Setter for the payment type ID of the payment.
   *
   * @param {Number} paymentTypeID The payment type ID of the payment.
   */
  set paymentTypeID(paymentTypeID) {
    this._model.set('paymentTypeID', paymentTypeID);
  }

  /**
   * Determine if the tip amount should be refunded.
   *
   * @returns {Boolean} Default value false
   */
  get refundTip() {
    return this._refundTip;
  }

  /**
   * Set if the tip amount should be refunded.
   *
   * @param {Boolean} refundTip
   */
  set refundTip(refundTip) {
    this._refundTip = refundTip;
  }

  /**
   * Getter for the maximumRefundAmount available of the payment.
   *
   * @returns {Decimal}    The maximumRefundAmount of the payment.
   */
  get maximumRefundAmount() {
    const amount = new Decimal(this.amount || 0);
    const tipAmount = new Decimal(this.tipAmount || 0);
    const refundedAmount = new Decimal(
      this.serviceModel.get('refundedAmount') || this.model.get('refunded') || 0
    );

    return amount
      .minus(refundedAmount)
      .minus(tipAmount)
      .abs();
  }

  /**
   * Commit the payment via the API
   *
   * @returns {jQuery.Promise}    Resolves when the API call completes
   */
  commit() {
    return this._model.save();
  }

  /**
   * Remove the payment using the API
   *
   * @returns {jQuery.Promise}    Resolves with the API call completes
   */
  remove() {
    this._model.set('archived', true);

    return this._model.save();
  }

  /**
   * Refunds the payment.  This only applies to payment service payments.
   *
   * @param {Decimal} amount  The amount to refund.
   */
  refund(amount) {
    let deferred = $.Deferred();
    if (this.isServicePayment()) {
      let servicePayment = new PaymentModel({
        paymentID: this._model.get('paymentID'),
      });

      servicePayment
        .fetch()
        .done(() => {
          // set the saleID to apply the refund to the appropriate sale
          servicePayment.set({
            saleID: this._model.get('saleID'),
          });

          if (servicePayment.get('status') === PaymentModel.Status.APPROVED) {
            servicePayment
              .refundPayment({ amount, refundTip: this.refundTip })
              .done(() => {
                deferred.resolve(servicePayment);
              })
              .fail(() => {
                deferred.reject();
              });
          } else {
            deferred.reject();
          }
        })
        .fail(() => {
          deferred.reject();
        });
    } else {
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Determines if the payment is managed by the payment service.  A payment service
   * payment would have a valid paymentID in the model.
   *
   * @returns {Boolean}   True if the payment is managed by the payment service
   */
  isServicePayment() {
    return this.model.has('paymentID') && this.model.get('paymentID').length > 0;
  }

  /**
   * Conform the JSON API to allow serialization of a Payment object.  This
   * exports the model as amodel property in a JSON response.
   *
   * @returns {model: Backbone.Model ,serviceModel: Backbone.Model , maximumRefundAmount: Decimal }
   */
  toJSON() {
    return {
      model: this.model.toJSON(),
      serviceModel: this.serviceModel.toJSON(),
      maximumRefundAmount: this.maximumRefundAmount,
    };
  }

  /**
   * Clones the payment
   *
   * @returns {Payment}   The cloned payment
   */
  clone() {
    // create a clone of the same class...
    let clone = new this.constructor();

    // then populate it's data
    clone.model = this.model.clone();
    clone.serviceModel = this.serviceModel.clone();
    clone._commitImmediately = this.commitImmediately;
    clone._allowMultiple = this.allowMultiple;

    return clone;
  }
}
